//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { ICollectionResponse } from '@apis/collections/ICollectionResponse';
import {
  CollectionMembershipState,
  ContentLibraryFilter,
  NetworkType,
} from '@apis/content-library/contentLibraryApiModel';
import { isDefined } from '@utils/filterPredicates';

export const INSTAGRAM_USER_NETWORK = NetworkType.FACEBOOK;

export const INSTAGRAM_NETWORK_LEAF = NetworkType.INSTAGRAM;

const nonEmpty = (values?: string[]): string[] =>
  (values ?? []).filter((value) => value.length > 0);

const toUserIdFilters = (
  userIds: string[],
  networkType: NetworkType,
): ContentLibraryFilter[] =>
  userIds.map((userId) => ({
    '@type': 'userId',
    userId,
    userIds: [userId],
    networkType,
  }));

const resolveUsernames = (
  ids: string[],
  metadata: Record<string, { username: string }> | undefined,
): string[] =>
  ids
    .map((id) => metadata?.[id]?.username)
    .filter((username): username is string => isDefined(username));

export const buildCollectionSourceFilters = (
  collection: ICollectionResponse,
): ContentLibraryFilter[] => {
  const { id, sources, sourcesMetadata } = collection;
  const filters: ContentLibraryFilter[] = [
    {
      '@type': 'collectionMembership',
      collectionId: id,
      state: CollectionMembershipState.Added,
    },
  ];

  const trackedUserIds = [
    ...nonEmpty(sources.instagramUsers),
    ...nonEmpty(sources.instagramBusinessUsers),
  ];
  filters.push(...toUserIdFilters(trackedUserIds, INSTAGRAM_USER_NETWORK));

  filters.push(
    ...toUserIdFilters(nonEmpty(sources.pinterestUsers), NetworkType.PINTEREST),
  );

  const hashtags = nonEmpty(sources.instagramHashtags);
  if (hashtags.length > 0) {
    filters.push({
      '@type': 'and',
      filters: [
        { '@type': 'network', networkTypes: [INSTAGRAM_NETWORK_LEAF] },
        { '@type': 'hashtags', hashtags },
      ],
    });
  }

  const instagramMentions = resolveUsernames(
    nonEmpty(sources.instagramMentions),
    sourcesMetadata?.instagramMentions,
  );
  if (instagramMentions.length > 0) {
    filters.push(
      {
        '@type': 'and',
        filters: [
          { '@type': 'network', networkTypes: [INSTAGRAM_NETWORK_LEAF] },
          { '@type': 'mentionedUser', usernames: instagramMentions },
        ],
      },
      {
        '@type': 'and',
        filters: [
          { '@type': 'network', networkTypes: [INSTAGRAM_NETWORK_LEAF] },
          { '@type': 'taggedUser', usernames: instagramMentions },
        ],
      },
    );
  }

  const tiktokMentions = resolveUsernames(
    nonEmpty(sources.tiktokMentions),
    sourcesMetadata?.tiktokMentions,
  );
  if (tiktokMentions.length > 0) {
    filters.push({
      '@type': 'and',
      filters: [
        { '@type': 'network', networkTypes: [NetworkType.TIKTOK] },
        { '@type': 'mentionedUser', usernames: tiktokMentions },
      ],
    });
  }

  const galleryUploaders = nonEmpty(sources.galleryUploaders);
  if (galleryUploaders.length > 0) {
    filters.push({ '@type': 'galleryUploader', reelIds: galleryUploaders });
  }

  return filters;
};

/**
 * Everything in the collection: items added to it or matching its sources,
 * minus items removed from it.
 */
export const buildCollectionScopeFilter = (
  collection: ICollectionResponse,
): ContentLibraryFilter => ({
  '@type': 'and',
  filters: [
    { '@type': 'or', filters: buildCollectionSourceFilters(collection) },
    {
      '@type': 'collectionMembership',
      collectionId: collection.id,
      state: CollectionMembershipState.Removed,
      negated: true,
    },
  ],
});
